import { cloneDeep as _cloneDeep, get as _get, isArray, set as _set } from 'lodash'
import { Component, Fragment, createRef, RefObject } from 'react'
import { api, BaseResource, ResourcePage } from 'stylewhere/api'
import { Box, Form, FormSchemaFormField, Spacer } from 'stylewhere/components'
import {
  AppStore,
  fetchAutocompleteDefaultValues,
  FormSchema,
  FormSchemaData,
  FormSchemaField,
  getFieldValue,
  OperationConfig,
  parseGS1Barcode,
  setFieldValue,
  Storage
} from 'stylewhere/shared'
import { getLanguage, T, __ } from 'stylewhere/shared/i18n'
import { isModalError, showToastError } from 'stylewhere/utils'

interface Props {
  initialValues?: FormSchemaData
  schema: FormSchema
  onSubmit: (data: FormSchemaData) => void | Promise<void>
  onCancel?: () => void
  submitText?: string
  cancelText?: string
  cancelDisabled?: boolean
  style?: React.CSSProperties
  disabled?: boolean
  formContext?: string
  flex?: boolean
  gridFieldColumn?: number
  operation?: OperationConfig
  formDataUpdated?: FormSchemaData
  cancelMarginTop?: number
  disabledSpacerAfterFields?: boolean
  // forceFetchAutocomplete?: boolean
  validateForm?: (data: FormSchemaData) => any
  children?: React.ReactNode
}

interface State {
  data: FormSchemaData
  initialValues?: FormSchemaData
  loading: boolean
  schema: FormSchema
  inputCheckField?: FormSchemaField
}

/**
 * Example
 * schema={[
        { placeholder: __(T.misc.username), name: 'username', required: true },
        { placeholder: __(T.misc.password), name: 'password', type: 'password', required: true },
    ]}
 */
export class FormSchemaForm extends Component<Props, State> {
  timer?: any
  inputRefs: RefObject<HTMLInputElement>[] = []
  currentFields: FormSchemaField[] = []

  state: State = {
    loading: true,
    data: {},
    schema: []
  }

  static applyDefaultValue(field: FormSchemaField, initialValues: FormSchemaData, initialValue: unknown) {
    if (field.defaultValue === undefined || initialValue !== undefined) return
    if (field.defaultValue === ':placeId') {
      setFieldValue(AppStore.defaultWorkstation?.placeId, initialValues, field)
      return
    }
    if (field.defaultValue === ':workstationId') {
      setFieldValue(AppStore.defaultWorkstation?.id, initialValues, field)
      return
    }
    if (field.defaultValue === ':workstationCode') {
      setFieldValue(AppStore.defaultWorkstation?.code, initialValues, field)
      return
    }
    setFieldValue(field.defaultValue, initialValues, field)
  }

  static stripDynamicFieldFormat(field: FormSchemaField, initialValues: FormSchemaData, initialValue: string) {
    if (!field.format?.dynamicField) return
    let data = _get(initialValues, field.format.dynamicField)
    if (!data) return
    const separator = field.format.separator || ''
    data = field.format.prefix ? data + separator : separator + data
    if (initialValue.includes(data)) {
      setFieldValue(initialValue.replace(data, ''), initialValues, field)
    }
  }

  static stripCharacterFormat(field: FormSchemaField, initialValues: FormSchemaData, initialValue: string) {
    if (!field.format?.character) return
    let index = field.format.prefix ? 0 : initialValue.length - 1
    let found = true
    let newValue = ''
    while (field.format.prefix ? index < initialValue.length : index >= 0) {
      if (!(initialValue[index] === field.format.character && found)) {
        found = false
        newValue = field.format.prefix ? newValue + initialValue[index] : initialValue[index] + newValue
      }
      index = field.format.prefix ? index + 1 : index - 1
    }
    setFieldValue(newValue, initialValues, field)
  }

  static applyRegexFormat(field: FormSchemaField, initialValues: FormSchemaData, initialValue: string) {
    if (!field.format?.regex) return
    const regex = new RegExp(field.format.regex)
    const match = initialValue.match(regex)
    if (match && match.length > 1) {
      setFieldValue(match[1], initialValues, field)
    }
  }

  static applyFieldFormatting(field: FormSchemaField, initialValues: FormSchemaData) {
    if (!field.format) return
    const initialValue = getFieldValue(initialValues, field)
    if (!initialValue) return
    const value = String(initialValue)
    if (field.format.dynamicField) {
      FormSchemaForm.stripDynamicFieldFormat(field, initialValues, value)
      return
    }
    if (field.format.character) {
      FormSchemaForm.stripCharacterFormat(field, initialValues, value)
      return
    }
    if (field.format.regex) {
      FormSchemaForm.applyRegexFormat(field, initialValues, value)
    }
  }

  static async preprocessFormData({ schema, initialValues }: { schema: FormSchema; initialValues: FormSchemaData }) {
    // Converti i defaultValues dei campi autocomplete
    schema = await fetchAutocompleteDefaultValues(schema)

    const dateFields = schema.filter((field) => field.type === 'date')
    for (const field of dateFields) {
      if (field.currentDateAsDefault) {
        field.defaultValue = new Date()
      }
    }

    // Imposta i valori iniziali dei campi
    schema.forEach((field) => {
      const initialValue = getFieldValue(initialValues, field)
      FormSchemaForm.applyDefaultValue(field, initialValues, initialValue)
    })

    schema.forEach((field) => {
      FormSchemaForm.applyFieldFormatting(field, initialValues)
    })
    return { schema, initialValues }
  }

  _init = async () => {
    const { schema, initialValues, operation } = this.props
    const getBaseStorageKey = () => (operation ? `form-${operation.type}-${operation.id}` : '')
    const loadInitValuesFromStoredFields = () => {
      const tmp = {}
      const baseKey = getBaseStorageKey()
      schema.forEach((obj) => {
        if (!obj.storage) return
        const storedData = Storage.load(`${baseKey}-${obj.name}`)
        if (!storedData) return
        if (obj.name.includes('.')) {
          const [parent, child] = obj.name.split('.')
          if (parent && child) {
            tmp[parent] = {
              ...tmp[parent],
              [child]: storedData
            }
            return
          }
        }
        tmp[obj.name] = storedData
      })
      return tmp
    }
    const sanitizeAutocompleteInitialValues = (values: any) => {
      schema
        .filter((s) => s.type === 'autocomplete')
        .forEach((el) => {
          const val = _get(values, el.name)
          if (val && typeof val === 'string') {
            _set(values, el.name, undefined)
          }
        })
    }
    const mergeMissingStoredFields = (values: any) => {
      if (!operation || operation.activeStorageForm) return
      const baseKey = getBaseStorageKey()
      schema.forEach((obj) => {
        if (!obj.storage || _get(values, obj.name)) return
        const storedData = Storage.load(`${baseKey}-${obj.name}`)
        if (storedData) {
          _set(values, obj.name, storedData)
        }
      })
    }

    let initValues = initialValues
    if (initValues) {
      sanitizeAutocompleteInitialValues(initValues)
      mergeMissingStoredFields(initValues)
    } else if (operation?.activeStorageForm) {
      initValues = Storage.load(getBaseStorageKey())
    } else if (operation) {
      initValues = loadInitValuesFromStoredFields()
    }
    const processed = await FormSchemaForm.preprocessFormData({ schema, initialValues: initValues || {} })
    this.setState(
      {
        initialValues: processed.initialValues,
        data: processed.initialValues,
        schema: processed.schema,
        loading: false
      },
      this.checkDirectSubmit
    )
  }

  componentDidMount() {
    this._init()
  }

  shouldComponentUpdate = (nextProps) => {
    const { data } = this.state
    if (nextProps.formDataUpdated && JSON.stringify(nextProps.formDataUpdated) !== JSON.stringify(data)) {
      this.setState({ data: nextProps.formDataUpdated })
    }
    return true
  }

  checkDirectSubmit = () => {
    const { schema } = this.state
    if (schema.length == 1 && schema[0].directSubmit) {
      this.handleSubmit(undefined)
    }
  }

  processFieldBeforeValidation = (
    field: FormSchemaField,
    data: FormSchemaData,
    schema: FormSchema,
    errors: string[]
  ) => {
    let value = getFieldValue(data, field)
    if (field.copyValueFrom) {
      const copyField = schema.find((record) => record.name === field.copyValueFrom)
      if (copyField) {
        value = getFieldValue(data, copyField)
        setFieldValue(value, data, field)
      }
    }
    if (field.type === 'gs1code') {
      const gs1fields = parseGS1Barcode(value)
      if (gs1fields.parsedCodeItems && gs1fields.parsedCodeItems.length > 0) {
        const separator = field.format?.separator || ''
        value = field.format?.keepGS1AIs
          ? gs1fields.parsedCodeItems.map((item: any) => `${item.ai}${item.data}`).join(separator)
          : gs1fields.parsedCodeItems.map((item: any) => item.data).join(separator)
        setFieldValue(value, data, field)
      } else {
        errors.push(__(T.error.gs1code_invalid))
      }
    }
    value = this.formattingText(getFieldValue(data, field), field)
    const currentValue =
      value && typeof value === 'object' && field.type === 'select' && field.confirmValuePath
        ? _get(value, field.confirmValuePath)
        : value
    setFieldValue(currentValue, data, field)
    return value
  }

  validateProcessedField = (field: FormSchemaField, value: any, errors: string[]) => {
    if (
      field.required &&
      (value === undefined || value === null || (typeof value === 'string' && value.trim() === ''))
    ) {
      errors.push(__(T.error.field_required, { label: field.label }))
    }
    if (field.validation?.regexp && !new RegExp(field.validation.regexp).test(value)) {
      errors.push(field.validation.errors?.[getLanguage()] ?? __(T.error.field_invalid, { field: field.label }))
    }
  }

  collectFieldErrors = (data: FormSchemaData) => {
    const errors: string[] = []
    const { schema } = this.state
    schema.forEach((field) => {
      const value = this.processFieldBeforeValidation(field, data, schema, errors)
      this.validateProcessedField(field, value, errors)
    })
    return errors
  }

  runValidationPipeline = (data: FormSchemaData) => {
    const { validateForm } = this.props
    let errors = this.collectFieldErrors(data)
    if (validateForm && errors.length === 0) {
      errors = validateForm(data)
    }
    return errors
  }

  showSubmitErrors = (error: string) => {
    const { operation } = this.props
    showToastError(error, __(T.error.error), operation ? isModalError(operation) : false)
  }

  persistStorageFields = (formatData: FormSchemaData) => {
    const { operation } = this.props
    if (!operation || operation.activeStorageForm) return
    const { schema } = this.state
    for (const field of schema) {
      if (!field.storage) continue
      const value = _get(formatData, field.name)
      if (value) {
        Storage.save('form-' + operation.type + '-' + operation.id + '-' + field.name, value)
      }
    }
  }

  submitData = async (data: FormSchemaData) => {
    const { onSubmit } = this.props
    const str = JSON.stringify(data)
    await onSubmit(JSON.parse(str))
  }

  handleSubmit = async (event: React.FormEvent | undefined) => {
    const { data, loading } = this.state
    if (event) event.preventDefault()
    if (loading) return
    this.setState({ loading: true })
    try {
      const formatData = _cloneDeep(data)
      this.persistStorageFields(formatData)
      const errors = this.runValidationPipeline(formatData)
      if (errors.length) {
        this.showSubmitErrors(errors[0])
        return
      }
      await this.submitData(formatData)
    } catch (error) {
      console.error(error)
    } finally {
      this.setState({ loading: false })
    }
  }

  _checkAutocompleteFields = (value: any, field: FormSchemaField) => {
    const { data, schema } = this.state
    //aggiungere gestione lato form schema per set valori in base al change della select
    if (field.autocompleteFields) {
      let objField, val
      for (const autocompleteField of field.autocompleteFields) {
        objField = schema.find((element) => {
          return element.name === autocompleteField.name
        })
        if (objField) {
          val = value?.[autocompleteField.field] || objField.defaultValue || ''
          setFieldValue(val, data, objField as FormSchemaField)
        }
      }
    }

    //si controlla se esiste un altro field dipendente da quello modificato
    let schemaField
    for (const currentSchemaField of schema) {
      schemaField = currentSchemaField
      if (schemaField?.dependencies) {
        const objDep = schemaField.dependencies.find((element) => {
          return field.name === element.whenField
        })
        if (objDep) {
          setFieldValue(undefined, data, schemaField as FormSchemaField)
        }
      }
    }
  }

  handleEnter = (value: any, field: FormSchemaField) => {
    const { data } = this.state
    setFieldValue(value, data, field)
    this.setState(data)
    if (field.submitOnEnter) {
      this.handleSubmit(undefined)
      return
    }

    const fields = this.currentFields
    if (!fields?.length) return

    // Se è impostato focusOnField usa quel campo come target
    const targetName = field.focusOnField

    let nextIndex = -1
    if (targetName) {
      nextIndex = fields.findIndex((f) => f.name === targetName)
    }

    // Fallback: campo successivo
    if (nextIndex === -1) {
      const currentIndex = fields.findIndex((f) => f.name === field.name)
      nextIndex = currentIndex + 1
    }

    // Salta eventuali campi hidden
    while (nextIndex < fields.length && fields[nextIndex].type === 'hidden') {
      nextIndex++
    }

    const nextRef = this.inputRefs[nextIndex]
    if (nextRef?.current) {
      nextRef.current.focus()
    }
  }

  applyDynamicFieldFormat = (value: string, field: FormSchemaField) => {
    if (!field.format?.dynamicField) return value
    const data = _get(this.state.data, field.format.dynamicField)
    if (!data) return value
    const separator = field.format.separator || ''
    return field.format.prefix ? data + separator + value : value + separator + data
  }

  applyCharacterFormat = (value: string, field: FormSchemaField) => {
    if (!field.format?.character) return value
    const minLength = field.format.minLength || 10
    if (value.length >= minLength) return value
    let formatted = value
    while (formatted.length < minLength) {
      formatted = field.format.prefix ? field.format.character + formatted : formatted + field.format.character
    }
    return formatted
  }

  applyRegexFormat = (value: string, field: FormSchemaField): string | undefined => {
    if (!field.format?.regex) return value
    const regex = new RegExp(field.format.regex)
    const match = value.match(regex)
    return match && match.length > 1 ? match[1] : undefined
  }

  formattingText = (value: string | undefined, field: FormSchemaField) => {
    if (value === undefined || !field.format) return value
    if (field.format.dynamicField) return this.applyDynamicFieldFormat(value, field)
    if (field.format.character) return this.applyCharacterFormat(value, field)
    if (field.format.regex) return this.applyRegexFormat(value, field)
    return value
  }

  handleChange = async (value: any, field: FormSchemaField) => {
    const { data } = this.state
    let checkAutoSubmit = true
    setFieldValue(value, data, field)
    // per i campi input si verifica se c'è un endpoint di verifica
    if (field.autocheckEndpoint && field.autocheckEndpointField) {
      checkAutoSubmit = false
      this.setState({ inputCheckField: field, loading: true }, this.handleChangeStartTimer) //EXP, STD, VET
    } else {
      this._checkAutocompleteFields(value, field)
    }
    this.setState(data)
    field.onChange?.(value, data, (newData: any) => this.setState(newData))
    if (checkAutoSubmit && field && field.autocheckFormSubmit && !field.submitOnEnter && value !== '') {
      this.setState({ inputCheckField: field, loading: true }, this.handleChangeStartTimer)
    }
  }

  handleChangeStartTimer = () => {
    this.handleChangeStopTimer()
    this.timer = setTimeout(() => {
      this.callAutocheckEndpoint()
    }, 500)
  }

  handleChangeStopTimer = () => {
    if (!this.timer) return
    clearTimeout(this.timer)
    this.timer = undefined
  }

  handleAutocheckEmptyResult = (data: FormSchemaData, field: FormSchemaField): boolean => {
    const checkAutoSubmit = this._checkEmptyResultAutocheckEndpoint(undefined, field)
    if (!checkAutoSubmit) {
      _set(data, field.name, '')
    }
    return checkAutoSubmit
  }

  fetchAndApplyAutocheckResult = async (data: FormSchemaData, field: FormSchemaField): Promise<boolean> => {
    const value = _get(data, field.name)
    if (!value || value === '') return this.handleAutocheckEmptyResult(data, field)
    const params = { [field.autocheckEndpointField]: value }
    const result = (await api.get<ResourcePage<BaseResource> | BaseResource[]>(field.autocheckEndpoint, params)).data
    if (result !== undefined && 'content' in result && result.content.length === 1) {
      this._checkAutocompleteFields(result.content[0], field)
      return true
    }
    return this.handleAutocheckEmptyResult(data, field)
  }

  callAutocheckEndpoint = async () => {
    const { data, inputCheckField } = this.state
    let checkAutoSubmit = true
    if (inputCheckField?.autocheckEndpoint && inputCheckField?.autocheckEndpointField) {
      checkAutoSubmit = await this.fetchAndApplyAutocheckResult(data, inputCheckField)
    }
    this.setState({ data, loading: false }, checkAutoSubmit ? this.checkAutoSubmit : undefined)
  }

  _checkEmptyResultAutocheckEndpoint = (value: any, field: FormSchemaField) => {
    if (!field.autocheckEndpointBlockIfEmpty) {
      this._checkAutocompleteFields(value, field)
      return true
    }
    showToastError(
      __(T.error.value_not_valid),
      __(T.error.error),
      this.props.operation ? isModalError(this.props.operation) : false
    )
    return false
  }

  checkAutoSubmit = () => {
    const { inputCheckField } = this.state
    if (inputCheckField?.autocheckFormSubmit) {
      this.handleSubmit(undefined)
    }
  }

  triggerDependency = (is: string, field: FormSchemaField, additionalValues: any, hide: boolean) => {
    const { data } = this.state
    if (is === 'disabled') {
      field.disabled = true
      // Svuota il campo quando lo disabiliti
      setFieldValue(null, data, field)
    } else if (is === 'enabled') {
      field.disabled = false
    } else if (is === 'emptied') {
      setFieldValue(null, data, field)
    } else if (is === 'filledWithValue') {
      setFieldValue(additionalValues, data, field)
    } else if (is === 'hidden') {
      // hide = true
    } else if (is === 'visible') {
      // hide = false
    } else if (is === 'optional') {
      field.required = false
    } else if (is === 'required') {
      field.required = true
    } else if (is === 'endpointReplaced' && field.type === 'autocomplete') {
      /**
       * Sostituisce parti dinamiche dell'endpoint con i valori del campo whenField corrispondenti
       * Quindi per esempio:
       * {
       *   name: 'foo',
       *   type: 'text',
       *  },
       * {
       *   name: 'bar',
       *   type: 'autocomplete',
       *   endpoint: 'some/url/?code=:fooCode',
       *   dependencies: [
       *     {
       *       is: 'endpointReplaced',
       *       endpointReplaced: { fooCode: '' },
       *       whenField: 'foo',
       *       condition: 'isFilled',
       *     },
       *   ],
       * }
       * Se il valore del campo foo è "ciao", l'endpoint del campo bar diventa "some/url/?code=ciao"
       * (Se fooCode è una stringa vuota, prende l'intero valore del campo. Se invece è una stringa,
       * tratta il campo come un oggetto e prende il valore nidificato con la funzione lodash _.get)
       */
      // Save original endpoint on first replacement
      const isFirstReplacement = !field.rawEndpoint
      if (!field.rawEndpoint) field.rawEndpoint = field.endpoint

      const previousEndpoint = field.endpoint
      // Reset to template before re-applying, otherwise replaced tokens are no longer found on value change
      field.endpoint = field.rawEndpoint

      Object.keys(additionalValues.replacements).forEach((token) => {
        const replacement = additionalValues.replacements[token]
        if (isArray(additionalValues.whenFieldValue)) {
          let additionalParams = ''
          additionalValues.whenFieldValue.forEach((whenFieldValue, index) => {
            const _rep = replacement ? _get(whenFieldValue, replacement) : additionalValues.whenFieldValue
            const tokenPrefix = index > 0 ? '&' + token + '=' : ''
            additionalParams += tokenPrefix + _rep
          })
          field.endpoint = field.endpoint.replaceAll(`:${token}`, additionalParams)
        } else {
          field.endpoint = field.endpoint.replaceAll(
            `:${token}`,
            replacement ? _get(additionalValues.whenFieldValue, replacement) : additionalValues.whenFieldValue
          )
        }
      })

      // Clear dependent field only on subsequent value changes, not on first render
      if (!isFirstReplacement && field.endpoint !== previousEndpoint) {
        setFieldValue(null, data, field)
      }
    }
  }

  negateIs = (is: string) => {
    if (is === 'disabled') return 'enabled'
    if (is === 'enabled') return 'disabled'
    if (is === 'hidden') return 'visible'
    if (is === 'visible') return 'hidden'
    if (is === 'optional') return 'required'
    if (is === 'required') return 'optional'
    return false
  }

  isDependencyConditionMatched = (dependency: any, whenFieldValue: any, whenField: FormSchemaField) => {
    const isEmpty = (v: any) => v === '' || v === null || v === undefined
    return (
      (dependency.condition === 'hasValue' && whenFieldValue === dependency.conditionValue) ||
      (dependency.condition === 'isEmpty' && isEmpty(whenFieldValue)) ||
      (dependency.condition === 'isFilled' && !isEmpty(whenFieldValue)) ||
      (dependency.condition === 'isSubmited' && whenField.submitted)
    )
  }

  getDependencyAdditionalOptions = (dependency: any, whenFieldValue: any) => {
    if (dependency.is === 'filledWithValue') return dependency.filledWithValue
    if (dependency.is === 'endpointReplaced') {
      return {
        whenFieldValue,
        replacements: dependency.endpointReplaced
      }
    }
    return undefined
  }

  applyFieldDependencies = (field: FormSchemaField, fields: FormSchemaField[], data: FormSchemaData) => {
    let hide = false
    if (!field.dependencies?.length) return hide
    field.dependencies.forEach((dependency) => {
      const whenField = fields.find(({ name }) => name === dependency.whenField)
      if (!whenField) return
      const whenFieldValue = getFieldValue(data, whenField)
      const additionalOptions = this.getDependencyAdditionalOptions(dependency, whenFieldValue)
      if (this.isDependencyConditionMatched(dependency, whenFieldValue, whenField)) {
        if (dependency.is === 'visible') hide = false
        else if (dependency.is === 'hidden') hide = true
        else this.triggerDependency(dependency.is, field, additionalOptions, hide)
        return
      }
      if (dependency.condition === 'isSubmited' && !whenField.submitted) {
        if (dependency.is === 'hidden') hide = true
        return
      }
      const negated = this.negateIs(dependency.is)
      if (!negated) return
      if (negated === 'hidden') hide = true
      else if (negated === 'visible') hide = false
      else this.triggerDependency(negated, field, additionalOptions, hide)
    })
    return hide
  }

  isAutocheckFormSubmit = () => {
    const { schema } = this.state
    if (schema) {
      const autoCheck = schema.find((field) => field.autocheckFormSubmit)
      return autoCheck
    }
    return false
  }

  isColumnsForm = (fields) => {
    const { gridFieldColumn } = this.props
    if (gridFieldColumn === 2) {
      return fields.filter((field) => field.type !== 'hidden').length > 3
    }
    return false
  }

  render() {
    const {
      submitText,
      style,
      onCancel,
      formContext,
      disabled,
      flex,
      operation,
      cancelText,
      cancelDisabled,
      cancelMarginTop,
      disabledSpacerAfterFields,
      children
    } = this.props
    const { data, initialValues, loading, schema } = this.state
    if (!data || !initialValues) return null
    const fields = schema.filter((field) => !field.hide)
    this.currentFields = fields
    const columns = this.isColumnsForm(fields)
    const isAutocheckFormSubmit = this.isAutocheckFormSubmit()
    return (
      <Form
        columns={columns}
        submitText={isAutocheckFormSubmit ? undefined : submitText}
        cancelText={cancelText}
        cancelDisabled={cancelDisabled}
        cancelMarginTop={cancelMarginTop || 30}
        style={style}
        onCancel={onCancel}
        onSubmit={this.handleSubmit}
        loading={loading}
        flex={flex}
      >
        {fields.map((field, index) => {
          const hide = this.applyFieldDependencies(field, fields, data)
          const inputRef = this.inputRefs[index] || (this.inputRefs[index] = createRef<HTMLInputElement>())

          return (
            <Fragment key={field.name + index}>
              <Box hidden={field.type === 'hidden'}>
                {!hide && (
                  <FormSchemaFormField
                    field={field}
                    index={index}
                    value={getFieldValue(data, field)}
                    defaultValue={getFieldValue(initialValues, field)}
                    disabled={disabled}
                    onChange={(v) => this.handleChange(v, field)}
                    onEnter={(v) => this.handleEnter(v, field)}
                    inputRef={inputRef}
                    formContext={formContext}
                    operation={operation}
                    formData={data}
                  />
                )}
                {!disabledSpacerAfterFields && <Spacer height={'10'} />}
              </Box>
              {field.addEmptyField && <Box />}
            </Fragment>
          )
        })}
        {children}
      </Form>
    )
  }
}
